import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "../../lib/api";
import { kvGet, kvSet } from "../../lib/db";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency } from "../../lib/romEngine";
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, CartesianGrid, Line,
} from "recharts";
import {
    ArrowRight, CurrencyDollar, TrendUp, Kanban, Clock, FileText,
    WarningOctagon, CalendarBlank, Target, Pulse, Receipt, ChartLineUp,
    CheckCircle, Bell, Gauge, Users, ArrowUp, ArrowDown, Sparkle, CircleNotch,
    ArrowClockwise, Eye, Lightning, Handshake, Trophy, ChartBar, Plus,
} from "@phosphor-icons/react";

const NAVY = "#0E1A24", ORANGE = "#FF6A13", GREEN = "#00853F", AMBER = "#d97706", RED = "#dc2626";
const HEALTH = {
    on_track: { label: "On track", color: GREEN }, at_risk: { label: "Watch", color: AMBER },
    off_track: { label: "Critical", color: RED },
};
const STAGE_LABEL = {
    rom: "ROM", review: "Review", go_no_go: "Go/No-Go", execution: "Execution",
    invoice: "Invoice", pay: "Payment", ar: "AR",
};
const compactCurrency = (n) => {
    if (n == null) return "—";
    const a = Math.abs(n);
    if (a >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (a >= 1e3) return `$${Math.round(n / 1e3)}k`;
    return `$${Math.round(n)}`;
};
const round2 = (n) => Math.round((n || 0) * 100) / 100;

export default function BusinessDashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [d, setD] = useState(null);
    const [err, setErr] = useState(false);
    const [offline, setOffline] = useState(false);

    useEffect(() => {
        endpoints.businessOverview().then((r) => {
            setD(r.data);
            setOffline(false);
            kvSet("business_dashboard_cache", r.data).catch(() => {});
        }).catch(async () => {
            // Offline (or the API is unreachable) — fall back to the last
            // successful response cached in IndexedDB rather than a hard
            // error, matching the offline-first behaviour of RomsOverview.
            const cached = await kvGet("business_dashboard_cache").catch(() => null);
            if (cached) {
                setD(cached);
                setOffline(true);
            } else {
                setErr(true);
            }
        });
    }, []);

    if (err) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--rom-text-3)]">Couldn’t load the dashboard. Reconnect and refresh.</div>;
    if (!d) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--rom-text-3)]">Loading the command center…</div>;

    const fin = d.is_finance;
    const h = d.headline;
    const hour = new Date().getHours();
    const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

    return (
        <div className="max-w-[1600px] mx-auto px-6 py-8 space-y-6" data-testid="business-dashboard">
            {/* Phone only — quick-start straight into a new commercial ROM on-site */}
            <button
                onClick={() => navigate("/calculator/new?scale=commercial")}
                className="btn btn-primary w-full justify-center sm:hidden"
                data-testid="command-center-new-rom-mobile"
            >
                <Plus size={16} weight="bold" /> New Commercial ROM
            </button>

            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">
                        Business Command Center
                    </div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
                        {greet}, {(user?.name || "").split(" ")[0] || "there"}
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">Here’s how the business is running right now.</p>
                </div>
            </header>

            {/* Restore AI business briefing (managers/admins) */}
            {fin && <AiBriefingCard cached={d.ai_briefing} />}

            {/* Won this month highlight */}
            {d.won_this_month && <WonThisMonthCard won={d.won_this_month} navigate={navigate} />}

            {/* Health score + Action center */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <HealthScoreCard hs={d.health_score} />
                <ActionCenter actions={d.actions} navigate={navigate} />
            </div>

            {/* Headline KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-grid">
                <Kpi icon={Kanban} label="Pipeline value" value={compactCurrency(h.pipeline_value)} sub={`${h.active_projects} active`} color={NAVY} onClick={() => navigate("/projects")} />
                {fin && <Kpi icon={TrendUp} label="Blended margin" value={h.blended_margin_pct != null ? `${h.blended_margin_pct}%` : "—"} sub={compactCurrency(h.total_margin)} color={h.blended_margin_pct < 0 ? RED : h.blended_margin_pct < 15 ? AMBER : GREEN} />}
                {fin && <Kpi icon={Receipt} label="Outstanding AR" value={compactCurrency(h.outstanding_ar)} sub="invoiced − paid" color={h.outstanding_ar > 0 ? AMBER : GREEN} />}
                <Kpi icon={Target} label="Quote win-rate" value={h.win_rate != null ? `${h.win_rate}%` : "—"} sub={`${h.proposals_decided} decided`} color={ORANGE} onClick={() => navigate("/documents")} />
                <Kpi icon={CurrencyDollar} label="Total ROM value" value={compactCurrency(h.total_rom_value)} sub={`${h.total_jobs} estimates`} color={NAVY} onClick={() => navigate("/estimates")} />
                <Kpi icon={Clock} label="Labour this week" value={compactCurrency(h.week_labour_cost)} sub={`${h.week_labour_hours}h logged`} color={ORANGE} onClick={() => navigate("/timesheets/entries")} />
            </div>

            {/* Trend + Funnel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <TrendCard trend={d.trend} fin={fin} />
                <FunnelCard funnel={d.funnel} fin={fin} navigate={navigate} />
            </div>

            {/* Losses by type */}
            <LossesByTypeCard byLossType={d.by_loss_type} fin={fin} navigate={navigate} />

            {/* Project health + Accuracy */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ProjectHealthCard ph={d.project_health} fin={fin} navigate={navigate} />
                <AccuracyCard acc={d.accuracy} navigate={navigate} />
            </div>

            {/* Timesheets + Scheduling + Cash */}
            <div className={`grid grid-cols-1 ${fin ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-6`}>
                <TimesheetsCard ts={d.timesheets} navigate={navigate} />
                <SchedulingCard sc={d.scheduling} navigate={navigate} />
                {fin && d.cash && <CashCard cash={d.cash} navigate={navigate} />}
            </div>
        </div>
    );
}

/* ── AI business briefing (CEO-level) ─────────────────────────────────── */
const BRIEF_HEALTH = { on_track: { c: GREEN, t: "On track" }, at_risk: { c: AMBER, t: "Watch" }, off_track: { c: RED, t: "Critical" } };

function AiBriefingCard({ cached }) {
    const [brief, setBrief] = useState(cached);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [open, setOpen] = useState(false);

    async function generate() {
        setBusy(true); setErr("");
        try {
            const { data } = await endpoints.businessBriefing();
            setBrief(data); setOpen(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t generate the briefing. Try again shortly.");
        } finally { setBusy(false); }
    }

    const meta = brief ? (BRIEF_HEALTH[brief.status] || BRIEF_HEALTH.at_risk) : null;
    return (
        <div className="rom-card overflow-hidden" data-testid="ai-briefing-card" style={{ borderTop: `3px solid ${ORANGE}` }}>
            <div className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap border-b border-[var(--rom-border)]">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-[8px] flex items-center justify-center shrink-0" style={{ background: "var(--rom-orange-soft)" }}>
                        <Sparkle size={20} weight="fill" style={{ color: ORANGE }} />
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--rom-text-3)]">Restore AI Business Briefing</div>
                        <div className="font-display font-black text-lg leading-tight">Your 2-minute operator’s read</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {brief && <button onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-text)] flex items-center gap-1" data-testid="briefing-toggle"><Eye size={13} weight="bold" /> {open ? "Hide" : "Show"}</button>}
                    <button onClick={generate} disabled={busy} data-testid="generate-briefing"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[6px] text-sm font-bold transition-transform hover:scale-[1.02] disabled:opacity-60"
                        style={{ background: ORANGE, color: "white" }}>
                        {busy ? <CircleNotch size={14} className="animate-spin" /> : brief ? <ArrowClockwise size={14} weight="bold" /> : <Sparkle size={14} weight="fill" />}
                        {busy ? "Analysing…" : brief ? "Refresh" : "Generate briefing"}
                    </button>
                </div>
            </div>

            {err && <div className="px-5 pb-4 pt-3 text-sm text-red-600">{err}</div>}
            {busy && !brief && <div className="px-5 py-5 text-sm text-[var(--rom-text-2)] flex items-center gap-2"><CircleNotch size={15} className="animate-spin" /> Reading every part of the business…</div>}
            {!brief && !busy && !err && (
                <div className="px-5 py-5 text-sm text-[var(--rom-text-2)]">Generate an executive read across pipeline, margins, projects, accuracy, labour and cash — with the priorities to drive this week.</div>
            )}

            {brief && open && (
                <div className="px-5 py-5 space-y-4" data-testid="briefing-body">
                    <div className="flex items-start gap-3 flex-wrap">
                        {meta && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold shrink-0" style={{ color: meta.c, background: `${meta.c}1a` }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.c }} />{meta.t}{brief.health_score != null ? ` · ${brief.health_score}` : ""}</span>}
                        <p className="font-display font-bold text-lg leading-snug flex-1 min-w-[240px]" data-testid="briefing-headline">{brief.headline}</p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                        {brief.wins?.length > 0 && <BriefBlock icon={<CheckCircle size={15} weight="fill" style={{ color: "var(--rom-navy)" }} />} title="Wins" items={brief.wins} />}
                        {brief.risks?.length > 0 && <BriefBlock icon={<WarningOctagon size={15} weight="fill" className="text-amber-500" />} title="Risks & leaks" items={brief.risks} />}
                    </div>
                    {brief.priorities?.length > 0 && <BriefBlock icon={<Lightning size={15} weight="fill" className="text-[var(--rom-orange)]" />} title="Priorities this week" items={brief.priorities} ordered />}
                    {brief.metric_to_watch && (
                        <div className="flex items-start gap-2 p-3 rounded-[6px] bg-[var(--rom-orange-soft)]/40 border border-[#fdd2b2]">
                            <Target size={16} weight="bold" className="text-[var(--rom-orange-dark)] shrink-0 mt-0.5" />
                            <div className="text-sm"><span className="font-bold">Metric to watch: </span>{brief.metric_to_watch}</div>
                        </div>
                    )}
                    <div className="text-[11px] text-[var(--rom-text-3)] pt-1">Generated {brief.generated_at ? new Date(brief.generated_at).toLocaleString() : ""}{brief.generated_by ? ` · ${brief.generated_by}` : ""}</div>
                </div>
            )}
        </div>
    );
}

function BriefBlock({ icon, title, items, ordered }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[var(--rom-text-2)] mb-1.5">{icon} {title}</div>
            <ul className="space-y-1.5">
                {items.map((it, i) => (
                    <li key={i} className="text-sm text-[var(--rom-text)] flex gap-2">
                        <span className="text-[var(--rom-text-3)] shrink-0 font-semibold">{ordered ? `${i + 1}.` : "•"}</span><span>{it}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ── Won this month ───────────────────────────────────────────────────── */
function WonThisMonthCard({ won, navigate }) {
    const monthName = new Date().toLocaleString("default", { month: "long" });
    if (won.count === 0) {
        return (
            <div className="rom-card" data-testid="won-this-month-card" style={{ borderLeft: `4px solid var(--rom-navy)` }}>
                <div className="rom-card-body flex items-center gap-3">
                    <Trophy size={22} weight="fill" style={{ color: "var(--rom-navy)" }} />
                    <div className="text-sm text-[var(--rom-text-2)]">No deals won yet in {monthName} — keep those proposals moving.</div>
                </div>
            </div>
        );
    }
    return (
        <div className="rom-card" data-testid="won-this-month-card" style={{ borderLeft: `4px solid var(--rom-navy)` }}>
            <div className="rom-card-body flex items-center gap-5 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-[8px] flex items-center justify-center shrink-0" style={{ background: "rgba(89,186,71,0.14)" }}>
                        <Trophy size={22} weight="fill" style={{ color: "var(--rom-navy)" }} />
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--rom-text-3)]">Won this month · {monthName}</div>
                        <div className="font-display font-black text-2xl leading-tight" style={{ color: "var(--rom-navy)" }} data-testid="won-this-month-value">
                            {won.count} deal{won.count > 1 ? "s" : ""} · {formatCurrency(won.value)}
                        </div>
                    </div>
                </div>
                {won.items?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap flex-1 justify-start lg:justify-end">
                        {won.items.map((it) => (
                            <button key={it.id} onClick={() => navigate("/documents")} data-testid={`won-item-${it.id}`}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-transform hover:scale-[1.03]"
                                style={{ background: "var(--rom-orange-soft)", color: "var(--rom-orange-dark)" }}>
                                <span className="font-mono">{it.ref || "ROM"}</span>
                                <span className="opacity-50">·</span>
                                <span>{it.recipient || "Client"}</span>
                                <span className="opacity-50">·</span>
                                <span>{compactCurrency(it.value)}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ── Health score ─────────────────────────────────────────────────────── */
function HealthScoreCard({ hs }) {
    const meta = HEALTH[hs.status] || HEALTH.at_risk;
    const data = [{ value: hs.score }, { value: 100 - hs.score }];
    return (
        <div className="rom-card" data-testid="health-score-card" style={{ borderTop: `3px solid ${meta.color}` }}>
            <div className="rom-card-body">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[var(--rom-text-3)] font-semibold mb-2">
                    <Gauge size={14} weight="bold" /> Business health
                </div>
                <div className="flex items-center gap-5">
                    <div className="relative w-[120px] h-[120px] shrink-0">
                        <PieChart width={120} height={120}>
                            <Pie data={data} dataKey="value" startAngle={90} endAngle={-270} innerRadius={44} outerRadius={56} stroke="none">
                                <Cell fill={meta.color} /><Cell fill="#eef1f4" />
                            </Pie>
                        </PieChart>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="font-display font-black text-3xl tracking-tight" data-testid="health-score-value" style={{ color: meta.color }}>{hs.score}</span>
                            <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: meta.color }}>{meta.label}</span>
                        </div>
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                        {hs.components.map((c) => (
                            <div key={c.label} title={c.detail}>
                                <div className="flex items-center justify-between text-[11px] mb-0.5">
                                    <span className="font-semibold text-[var(--rom-text-2)]">{c.label}</span>
                                    <span className="font-mono font-bold">{c.score}</span>
                                </div>
                                <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${c.score}%`, background: c.score >= 75 ? GREEN : c.score >= 50 ? AMBER : RED }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {hs.weakest?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[var(--rom-border)] text-xs text-[var(--rom-text-2)]">
                        <span className="font-semibold text-[var(--rom-text)]">Biggest drag:</span> {hs.weakest[0].detail}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ── Action center ────────────────────────────────────────────────────── */
function ActionCenter({ actions, navigate }) {
    const tiles = [
        { key: "change_requests", label: "Changes requested", icon: WarningOctagon, color: ORANGE, to: "/documents?focus=changes_requested" },
        { key: "proposals_awaiting", label: "Proposals awaiting", icon: FileText, color: NAVY, to: "/documents?focus=awaiting" },
        { key: "pending_timesheets", label: "Timesheets to approve", icon: Clock, color: AMBER, to: "/timesheets/approvals" },
        { key: "at_risk_projects", label: "At-risk projects", icon: Pulse, color: RED, to: "/projects?filter=at_risk" },
        { key: "overdue_tasks", label: "Overdue tasks", icon: CheckCircle, color: RED, to: "/projects?filter=overdue_tasks" },
        { key: "account_followups", label: "Account follow-ups", icon: Handshake, color: NAVY, to: "/crm?filter=followups" },
    ];
    const total = tiles.reduce((s, t) => s + (actions[t.key] || 0), 0);
    return (
        <div className="rom-card lg:col-span-2" data-testid="action-center">
            <div className="rom-card-head">
                <h3 className="flex items-center gap-2"><Bell size={16} weight="bold" className="text-[var(--rom-orange)]" /> Action center</h3>
                <span className="text-xs text-[var(--rom-text-3)]">{total === 0 ? "All clear 🎉" : `${total} item${total > 1 ? "s" : ""} need attention`}</span>
            </div>
            <div className="rom-card-body grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {tiles.map((t) => {
                    const n = actions[t.key] || 0;
                    return (
                        <button key={t.key} onClick={() => navigate(t.to)} data-testid={`action-${t.key}`}
                            className={`text-left p-3 rounded-[6px] border transition-all hover:shadow-sm ${n > 0 ? "border-[var(--rom-border-strong)] bg-white hover:border-[var(--rom-navy)]" : "border-[var(--rom-border)] bg-slate-50/60 opacity-70"}`}>
                            <t.icon size={18} weight="bold" style={{ color: n > 0 ? t.color : "var(--rom-text-3)" }} />
                            <div className="font-display font-black text-2xl mt-1.5" style={{ color: n > 0 ? t.color : "var(--rom-text-3)" }}>{n}</div>
                            <div className="text-[11px] text-[var(--rom-text-2)] leading-tight mt-0.5">{t.label}</div>
                        </button>
                    );
                })}
            </div>
            {actions.change_request_items?.length > 0 && (
                <div className="px-5 pb-4 -mt-1 space-y-1.5">
                    {actions.change_request_items.map((c) => (
                        <button key={c.id} onClick={() => c.job_id && navigate(`/calculator/${c.job_id}`)}
                            className="w-full flex items-center justify-between gap-3 px-3 py-1.5 rounded-[4px] bg-[var(--rom-orange-soft)]/40 hover:bg-[var(--rom-orange-soft)] border border-[#fdd2b2] transition-colors text-left text-xs">
                            <span className="font-mono font-semibold text-[var(--rom-text-2)]">{c.ref || "ROM"}</span>
                            <span className="text-[var(--rom-text-3)] truncate">{c.recipient || "Recipient"} requested changes</span>
                            <ArrowRight size={12} weight="bold" className="text-[var(--rom-orange-dark)] shrink-0" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── Trend ────────────────────────────────────────────────────────────── */
function TrendCard({ trend, fin }) {
    return (
        <div className="rom-card lg:col-span-2" data-testid="trend-card">
            <div className="rom-card-head">
                <h3 className="flex items-center gap-2"><ChartLineUp size={16} weight="bold" /> Performance over time</h3>
                <span className="text-xs text-[var(--rom-text-3)]">last 6 months</span>
            </div>
            <div className="rom-card-body">
                <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="gRom" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={ORANGE} stopOpacity={0.35} /><stop offset="100%" stopColor={ORANGE} stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gAct" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={NAVY} stopOpacity={0.25} /><stop offset="100%" stopColor={NAVY} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={compactCurrency} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={48} />
                        <Tooltip formatter={(v, n) => [formatCurrency(v), n === "rom_value" ? "ROM created" : "Actuals"]} labelStyle={{ fontWeight: 700 }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                        <Area type="monotone" dataKey="rom_value" stroke={ORANGE} strokeWidth={2} fill="url(#gRom)" name="rom_value" />
                        <Area type="monotone" dataKey="actual_value" stroke={NAVY} strokeWidth={2} fill="url(#gAct)" name="actual_value" />
                    </AreaChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-5 justify-center text-[11px] text-[var(--rom-text-2)] mt-1">
                    <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full" style={{ background: ORANGE }} /> ROM value created</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full" style={{ background: NAVY }} /> Actuals recorded</span>
                </div>
            </div>
        </div>
    );
}

/* ── Funnel ───────────────────────────────────────────────────────────── */
function FunnelCard({ funnel, fin, navigate }) {
    const data = funnel.map((f) => ({ ...f, name: STAGE_LABEL[f.stage] || f.stage }));
    return (
        <div className="rom-card" data-testid="funnel-card">
            <div className="rom-card-head">
                <h3 className="flex items-center gap-2"><Kanban size={16} weight="bold" /> Pipeline funnel</h3>
                <button className="text-xs text-[var(--rom-orange-dark)] font-semibold hover:underline flex items-center gap-1" onClick={() => navigate("/projects")}>Open <ArrowRight size={11} weight="bold" /></button>
            </div>
            <div className="rom-card-body">
                <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} width={70} />
                        <Tooltip formatter={(v, n, p) => fin ? [`${p.payload.count} · ${formatCurrency(p.payload.value)}`, "Projects"] : [`${p.payload.count}`, "Projects"]} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f8fafc" }} />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={18}>
                            {data.map((e, i) => <Cell key={i} fill={i < 3 ? NAVY : i < 5 ? ORANGE : GREEN} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

/* ── Losses by type ──────────────────────────────────────────────────── */
const STATUS_OPTS = [
    { v: "active", label: "Active" }, { v: "won", label: "Won" },
    { v: "closed", label: "Closed" }, { v: "all", label: "All" },
];
const RANGE_OPTS = [
    { v: "month", label: "This month" }, { v: "q", label: "Last 3 months" },
    { v: "year", label: "Last 12 months" }, { v: "all", label: "All time" },
];
function _inRange(createdAt, range) {
    if (range === "all") return true;
    if (!createdAt) return false;
    const created = new Date(createdAt);
    if (isNaN(created.getTime())) return false;
    const now = new Date();
    if (range === "month") return created.getFullYear() === now.getFullYear() && created.getMonth() === now.getMonth();
    const days = range === "q" ? 90 : 365;
    return (now - created) / 86400000 <= days;
}
function LossesByTypeCard({ byLossType, fin, navigate }) {
    const [status, setStatus] = useState("active");
    const [range, setRange] = useState("all");
    const [metric, setMetric] = useState("count");
    const effectiveMetric = fin ? metric : "count";

    const rows = (byLossType || []).map((b) => {
        const projs = b.projects.filter((p) =>
            (status === "all" || (status === "active" && p.is_active) || (status === "won" && p.is_won) || (status === "closed" && !p.is_active)) &&
            _inRange(p.created_at, range));
        return {
            loss_type: b.loss_type, name: b.loss_type === "unspecified" ? "Unspecified" : b.loss_type[0].toUpperCase() + b.loss_type.slice(1),
            count: projs.length, value: round2(projs.reduce((s, p) => s + (p.value || 0), 0)),
        };
    }).filter((r) => r.count > 0).sort((a, b) => (effectiveMetric === "value" ? b.value - a.value : b.count - a.count));

    const totalForShare = rows.reduce((s, r) => s + r[effectiveMetric], 0) || 1;
    rows.forEach((r) => { r.pctLabel = `${Math.round((r[effectiveMetric] / totalForShare) * 100)}%`; });

    return (
        <div className="rom-card" data-testid="losses-by-type-card">
            <div className="rom-card-head flex-wrap gap-2">
                <h3 className="flex items-center gap-2"><ChartBar size={16} weight="bold" /> Losses by type</h3>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="chip chip-navy text-[10px]" data-testid="losses-active-filter-badge">
                        {STATUS_OPTS.find((s) => s.v === status)?.label} · {RANGE_OPTS.find((r) => r.v === range)?.label}
                    </span>
                    {fin && (
                        <div className="flex rounded-[4px] border border-[var(--rom-border-strong)] overflow-hidden text-[11px] font-semibold" data-testid="losses-metric-toggle">
                            <button onClick={() => setMetric("count")} className={`px-2 py-1 ${metric === "count" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}>Count</button>
                            <button onClick={() => setMetric("value")} className={`px-2 py-1 ${metric === "value" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}>Value</button>
                        </div>
                    )}
                </div>
            </div>
            <div className="rom-card-body">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                    <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="losses-status-filter"
                        className="h-7 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs font-semibold bg-white">
                        {STATUS_OPTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                    <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="losses-range-filter"
                        className="h-7 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs font-semibold bg-white">
                        {RANGE_OPTS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                    </select>
                </div>
                {rows.length === 0 ? (
                    <div className="text-center text-sm text-[var(--rom-text-3)] py-12">No projects yet</div>
                ) : (
                    <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 44)}>
                        <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                            <XAxis type="number" hide />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} width={80} />
                            <Tooltip
                                formatter={(v, n, p) => fin
                                    ? [`${p.payload.count} projects, ${compactCurrency(p.payload.value)}`, ""]
                                    : [`${p.payload.count} projects`, ""]}
                                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f8fafc" }} />
                            <Bar dataKey={effectiveMetric} radius={[0, 4, 4, 0]} barSize={20} cursor="pointer"
                                onClick={(entry) => navigate(`/projects?loss_type=${encodeURIComponent(entry.loss_type)}`)}
                                label={(props) => (
                                    <text x={props.x + props.width - 8} y={props.y + props.height / 2} textAnchor="end" dominantBaseline="middle"
                                        fill="#fff" fontSize={11} fontWeight={700}>
                                        {rows[props.index]?.pctLabel}
                                    </text>
                                )}>
                                {rows.map((r, i) => <Cell key={i} fill={i === 0 ? ORANGE : NAVY} data-testid={`losses-bar-${r.loss_type}`} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}


function ProjectHealthCard({ ph, fin, navigate }) {
    const c = ph.counts;
    const data = [
        { name: "On track", value: c.on_track, color: GREEN },
        { name: "At risk", value: c.at_risk, color: AMBER },
        { name: "Off track", value: c.off_track, color: RED },
    ].filter((x) => x.value > 0);
    const total = c.on_track + c.at_risk + c.off_track;
    return (
        <div className="rom-card" data-testid="project-health-card">
            <div className="rom-card-head"><h3 className="flex items-center gap-2"><Pulse size={16} weight="bold" /> Project health</h3>
                <span className="text-xs text-[var(--rom-text-3)]">{ph.avg_progress_pct != null ? `avg ${Math.round(ph.avg_progress_pct)}% complete` : ""}</span></div>
            <div className="rom-card-body flex gap-5">
                <div className="relative w-[120px] h-[120px] shrink-0">
                    {total > 0 ? (
                        <>
                            <PieChart width={120} height={120}>
                                <Pie data={data} dataKey="value" innerRadius={40} outerRadius={56} stroke="none" paddingAngle={2}>
                                    {data.map((e, i) => <Cell key={i} fill={e.color} />)}
                                </Pie>
                            </PieChart>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="font-display font-black text-2xl">{total}</span>
                                <span className="text-[9px] uppercase tracking-wider text-[var(--rom-text-3)] font-bold">active</span>
                            </div>
                        </>
                    ) : <div className="flex items-center justify-center h-full text-xs text-[var(--rom-text-3)]">No projects</div>}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex gap-3 mb-2 text-xs">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: GREEN }} /> {c.on_track}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: AMBER }} /> {c.at_risk}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: RED }} /> {c.off_track}</span>
                    </div>
                    <div className="space-y-1">
                        {ph.at_risk.length === 0 ? (
                            <div className="text-xs text-[var(--rom-text-3)] py-2">No at-risk projects. Nicely run.</div>
                        ) : ph.at_risk.slice(0, 4).map((r) => (
                            <button key={r.id} onClick={() => navigate(`/projects/${r.id}`)} data-testid={`dash-at-risk-${r.id}`}
                                className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-[4px] bg-slate-50 hover:bg-slate-100 border border-[var(--rom-border)] transition-colors text-left">
                                <span className="flex items-center gap-1.5 min-w-0">
                                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: r.status === "off_track" ? RED : AMBER }} />
                                    <span className="text-xs font-medium truncate">{r.name}</span>
                                </span>
                                {fin && r.margin_pct != null && <span className="text-[11px] font-mono font-semibold shrink-0" style={{ color: r.margin_pct < 0 ? RED : AMBER }}>{r.margin_pct}%</span>}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Accuracy ─────────────────────────────────────────────────────────── */
function AccuracyCard({ acc, navigate }) {
    const edge = acc.estimator_edge || {};
    return (
        <div className="rom-card" data-testid="accuracy-card">
            <div className="rom-card-head"><h3 className="flex items-center gap-2"><Target size={16} weight="bold" /> Estimating accuracy</h3>
                <button className="text-xs text-[var(--rom-orange-dark)] font-semibold hover:underline flex items-center gap-1" onClick={() => navigate("/calibration")}>Tuner <ArrowRight size={11} weight="bold" /></button></div>
            <div className="rom-card-body">
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <Mini label="Avg variance" value={acc.avg_variance_pct != null ? `${acc.avg_variance_pct > 0 ? "+" : ""}${acc.avg_variance_pct}%` : "—"} color={acc.avg_variance_pct == null ? NAVY : Math.abs(acc.avg_variance_pct) < 10 ? GREEN : AMBER} />
                    <Mini label="Accuracy (MAE)" value={acc.mae_pct != null ? `±${acc.mae_pct}%` : "—"} color={NAVY} />
                    <Mini label="Closed jobs" value={acc.with_actuals} color={NAVY} />
                </div>
                <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold mb-1.5">Variance by loss type</div>
                <div className="space-y-1.5 mb-3">
                    {acc.by_loss.map((l) => {
                        const v = l.avg_variance_pct;
                        const w = v == null ? 0 : Math.min(50, Math.abs(v));
                        return (
                            <div key={l.loss_type} className="flex items-center gap-2 text-xs">
                                <span className="w-12 capitalize text-[var(--rom-text-2)]">{l.loss_type}</span>
                                <div className="flex-1 h-4 relative bg-slate-100 rounded">
                                    <div className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-300" />
                                    {v != null && <div className="absolute top-0 bottom-0 rounded" style={{ background: v >= 0 ? AMBER : GREEN, width: `${w}%`, left: v >= 0 ? "50%" : `${50 - w}%` }} />}
                                </div>
                                <span className="w-14 text-right font-mono font-semibold">{v != null ? `${v > 0 ? "+" : ""}${v}%` : `—`}</span>
                                <span className="w-6 text-right text-[var(--rom-text-3)]">{l.samples}</span>
                            </div>
                        );
                    })}
                </div>
                {edge.samples > 0 && (
                    <div className="pt-3 border-t border-[var(--rom-border)] flex items-center justify-between text-xs">
                        <span className="text-[var(--rom-text-2)]">Estimator’s edge — beat the raw engine on</span>
                        <span className="font-display font-black text-lg text-[var(--rom-orange-dark)]">{edge.beat_engine_pct != null ? `${edge.beat_engine_pct}%` : "—"}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ── Timesheets ───────────────────────────────────────────────────────── */
function TimesheetsCard({ ts, navigate }) {
    return (
        <div className="rom-card" data-testid="timesheets-card">
            <div className="rom-card-head"><h3 className="flex items-center gap-2"><Clock size={16} weight="bold" /> Timesheets · this week</h3>
                {ts.pending_count > 0 && <button onClick={() => navigate("/timesheets/approvals")} className="chip chip-orange text-[10px] hover:opacity-80" data-testid="ts-pending-chip">{ts.pending_count} to approve</button>}</div>
            <div className="rom-card-body">
                <div className="flex items-baseline gap-4 mb-3">
                    <div><div className="font-display font-black text-2xl">{ts.week_hours}h</div><div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">logged</div></div>
                    <div><div className="font-display font-black text-2xl text-[var(--rom-orange-dark)]">{compactCurrency(ts.week_cost)}</div><div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">labour cost</div></div>
                    <div className="ml-auto flex items-center gap-1 text-xs text-[var(--rom-text-2)]"><Users size={13} weight="bold" /> {ts.active_employees} crew</div>
                </div>
                <ResponsiveContainer width="100%" height={90}>
                    <BarChart data={ts.by_day} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(v) => [`${v}h`, "Hours"]} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f8fafc" }} />
                        <Bar dataKey="hours" fill={ORANGE} radius={[3, 3, 0, 0]} barSize={20} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

/* ── Scheduling ───────────────────────────────────────────────────────── */
function SchedulingCard({ sc, navigate }) {
    return (
        <div className="rom-card" data-testid="scheduling-card">
            <div className="rom-card-head"><h3 className="flex items-center gap-2"><CalendarBlank size={16} weight="bold" /> Schedule · next 7 days</h3>
                <button className="text-xs text-[var(--rom-orange-dark)] font-semibold hover:underline flex items-center gap-1" onClick={() => navigate("/schedules")}>Calendar <ArrowRight size={11} weight="bold" /></button></div>
            <div className="rom-card-body">
                <div className="mb-2 text-xs"><span className="font-display font-black text-xl text-[var(--rom-navy)]">{sc.on_site_today}</span> <span className="text-[var(--rom-text-2)]">crew on site today</span></div>
                <div className="space-y-1.5">
                    {sc.upcoming.length === 0 ? (
                        <div className="text-xs text-[var(--rom-text-3)] py-3 text-center">Nothing scheduled. Add site visits in the calendar.</div>
                    ) : sc.upcoming.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-[4px] bg-slate-50 border border-[var(--rom-border)] text-xs">
                            <span className="font-mono text-[10px] text-[var(--rom-text-3)] w-12 shrink-0">{fmtShort(s.date)}</span>
                            <span className={`chip text-[9px] ${s.type === "site" ? "chip-orange" : s.type === "site_visit" ? "chip-navy" : "chip-gray"}`} style={{ padding: "0px 5px" }}>{(s.type || "").replace("_", " ")}</span>
                            <span className="font-medium truncate flex-1">{s.title || s.project_name || "—"}</span>
                            {s.employee_name && <span className="text-[var(--rom-text-3)] truncate max-w-[80px]">{s.employee_name}</span>}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ── Cash & AR ────────────────────────────────────────────────────────── */
function CashCard({ cash, navigate }) {
    const pct = cash.collection_rate ?? 0;
    return (
        <div className="rom-card" data-testid="cash-card">
            <div className="rom-card-head"><h3 className="flex items-center gap-2"><CurrencyDollar size={16} weight="bold" /> Cash & AR</h3>
                <span className="text-xs text-[var(--rom-text-3)]">{cash.collection_rate != null ? `${cash.collection_rate}% collected` : ""}</span></div>
            <div className="rom-card-body">
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <Mini label="Invoiced" value={compactCurrency(cash.invoiced)} color={NAVY} />
                    <Mini label="Collected" value={compactCurrency(cash.paid)} color={GREEN} />
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden mb-1"><div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: GREEN }} /></div>
                <div className="flex justify-between text-[11px] text-[var(--rom-text-2)] mb-3"><span>Outstanding AR</span><span className="font-mono font-bold text-[var(--rom-orange-dark)]">{compactCurrency(cash.outstanding_ar)}</span></div>
                {cash.top_debtors.length > 0 && (
                    <>
                        <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold mb-1.5">Top outstanding</div>
                        <div className="space-y-1">
                            {cash.top_debtors.map((dbt) => (
                                <button key={dbt.id} onClick={() => navigate(`/projects/${dbt.id}`)} className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-[4px] hover:bg-slate-50 border border-transparent hover:border-[var(--rom-border)] transition-colors text-left">
                                    <span className="text-xs font-medium truncate">{dbt.name}</span>
                                    <span className="text-xs font-mono font-semibold text-[var(--rom-orange-dark)] shrink-0">{compactCurrency(dbt.ar)}</span>
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/* ── Primitives ───────────────────────────────────────────────────────── */
function Kpi({ icon: Icon, label, value, sub, color, onClick }) {
    return (
        <button onClick={onClick} disabled={!onClick} className={`rom-card text-left ${onClick ? "hover:shadow-md transition-shadow cursor-pointer" : ""}`} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="rom-card-body py-3.5">
                <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--rom-text-2)] font-semibold">{label}</span>
                    <Icon size={15} weight="bold" style={{ color }} />
                </div>
                <div className="mt-1.5 font-display font-black text-2xl tracking-tight" style={{ color }}>{value}</div>
                {sub && <div className="text-[11px] text-[var(--rom-text-3)] mt-0.5">{sub}</div>}
            </div>
        </button>
    );
}

function Mini({ label, value, color }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">{label}</div>
            <div className="font-display font-bold text-lg mt-0.5" style={{ color: color || "var(--rom-text)" }}>{value}</div>
        </div>
    );
}

function fmtShort(s) {
    try { const d = new Date(s + "T00:00:00"); return `${d.getDate()}/${d.getMonth() + 1}`; } catch { return s; }
}
